import React, { useEffect } from 'react'
import { useMediaState } from 'hooks/useMediaState'
import { Controller, useFormContext } from 'react-hook-form'
import { FormControl } from '@chakra-ui/core'
import MediaGallery from 'components/ui/MediaGallery'
import HiddenFileInput from 'components/ui/Input/HiddenFileInput'
import { Surface } from 'components/ui/Surface'
import useLocaleText from 'components/useLocaleText'
import DescriptionTextarea from 'components/TasksV2/TaskCreation/Steps/SubtaskCreationStep/Description/DescriptionTextarea'
import { AddMediaButtonts } from 'components/TasksV2/TaskCreation/Steps/SubtaskCreationStep/Description/AddMediaButtonts'
import { useDropzoneHandlers } from 'hooks/useDropzoneHandlers'
import strings from 'constants/strings'
import { DropzoneWrapper } from 'components/DropzoneWrapper'
import { MicButton } from 'components/Setup/SetupModal/styles'
import { pulseAnimation } from 'components/Setup/SetupModal/constants'
import { useSpeechInput } from 'components/Setup/SetupModal/hooks/useSpeechInput'
import { IoMic } from 'react-icons/io5'

interface DescriptionProps {
  speechEnabled: boolean
  descPath: string
  imagesPath: string
  videosPath: string
  filesPath: string
}

const Description: React.FC<DescriptionProps> = ({ speechEnabled, descPath, imagesPath, videosPath, filesPath }) => {
  const { getValues, setValue, control, register, watch } = useFormContext()
  const t_say_something = useLocaleText('t_say_something')

  const descValue: string = watch(descPath) || ''
  const { listening, speechSupported, isMicrophoneAvailable, startListening, stopListening, handleManualInput } =
    useSpeechInput({
      value: descValue,
      onChange: (value: string) => setValue(descPath, value),
      isOpen: speechEnabled,
    })

  const {
    uploadedImagesURL,
    uploadedVideosURL,
    uploadedFilesURL,
    handleMediaUploading,
    handleDeleteMedia,
    deleteFileHandler,
    handleFileUpload: uploadFile,
    imageInputRef,
    fileInputRef,
    isUploadLoading,
  } = useMediaState({
    images: getValues(imagesPath) || [],
    files: getValues(filesPath) || [],
    videos: getValues(videosPath) || [],
  })

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const data = await handleMediaUploading(event)
    if (!data || (!data.uploadedImages.length && !data.uploadedVideos.length)) {
      return
    }

    if (data.uploadedImages.length) {
      const newImages = [...uploadedImagesURL, ...data.uploadedImages]
      setValue(imagesPath, newImages)
    }
    if (data.uploadedVideos.length) {
      const newVideos = [...uploadedVideosURL, ...data.uploadedVideos]
      setValue(videosPath, newVideos)
    }
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = await uploadFile(event)
    if (!uploadedFiles || !uploadedFiles.length) {
      return
    }
    const newFiles = [...uploadedFilesURL, ...uploadedFiles.map((file) => file.fileUrl)]
    setValue(filesPath, newFiles)
  }

  const handleRemoveMedia = (index: number) => {
    const { filteredImages, filteredVideos } = handleDeleteMedia(index)
    setValue(videosPath, filteredVideos)
    setValue(imagesPath, filteredImages)
  }

  const handleRemoveFile = (url: string) => {
    const updatedFiles = deleteFileHandler(url)
    setValue(filesPath, updatedFiles)
  }

  const handleMediaUploadClick = () => {
    imageInputRef.current?.click()
  }

  const handleFileUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleUploadMedia = async (files: File[]) => {
    if (!files.length) return

    const imageVideos: File[] = []
    const otherFiles: File[] = []

    files.forEach((file) => {
      const fileType = file.type
      if (fileType.startsWith('image') || fileType.startsWith('video')) {
        imageVideos.push(file)
      } else {
        otherFiles.push(file)
      }
    })

    const uploadPromises = []

    if (imageVideos.length > 0) {
      const event = {
        target: {
          files: imageVideos as unknown as FileList,
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>
      uploadPromises.push(handleImageUpload(event))
    }

    if (otherFiles.length > 0) {
      const event = {
        currentTarget: {
          files: otherFiles as unknown as FileList,
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>
      uploadPromises.push(handleFileUpload(event))
    }

    await Promise.all(uploadPromises)
  }

  const { onDrop, handlePaste } = useDropzoneHandlers({
    onUploadMedia: handleUploadMedia,
    accept: `${strings.POST_FILE_ACCEPT_EXTENSIONS},image/*,video/*`,
  })

  useEffect(() => {
    register(imagesPath)
    register(videosPath)
    register(filesPath)
  }, [filesPath, imagesPath, register, videosPath])

  return (
    <FormControl mb="60px" w="100%">
      <DropzoneWrapper onDrop={onDrop} onPaste={handlePaste}>
        <Surface flexDir={'column'} pos="relative" p="15px">
          <style>{pulseAnimation}</style>
          {speechSupported && (
            <MicButton
              position="absolute"
              bottom="-40px"
              left="50%"
              px={0}
              transform="translateX(-50%)"
              isListening={listening}
              isDisabled={!speechEnabled || isMicrophoneAvailable === false}
              w="72px !important"
              h="72px !important"
              onClick={listening ? stopListening : startListening}
              title={
                isMicrophoneAvailable === false
                  ? 'Microphone is not available'
                  : listening
                  ? 'Stop recording'
                  : 'Start recording'
              }
            >
              <IoMic size={46} />
            </MicButton>
          )}
          <Controller
            name={descPath}
            control={control}
            defaultValue={getValues(descPath) || ''}
            as={DescriptionTextarea}
            onChange={([event]) => {
              handleManualInput()
              return event.target.value
            }}
            onBlur={([event]) => event.target.value}
            value={getValues(descPath) as string}
            placeholder={t_say_something}
            onImageUploadClick={handleMediaUploadClick}
            onFileUploadClick={handleFileUploadClick}
          />
          {/* Media Gallery */}
          <MediaGallery
            attachedImages={uploadedImagesURL}
            attachedFiles={uploadedFilesURL}
            attachedVideos={uploadedVideosURL}
            onRemoveMedia={handleRemoveMedia}
            onRemoveFile={handleRemoveFile}
            isUploadLoading={isUploadLoading}
          />
          <AddMediaButtonts onFileUploadClick={handleFileUploadClick} onImageUploadClick={handleMediaUploadClick} />
          {/* Hidden File Inputs */}
          <HiddenFileInput accept="image/*, video/*" multiple onChange={handleImageUpload} ref={imageInputRef} />
          <HiddenFileInput accept="*/*" onChange={handleFileUpload} ref={fileInputRef} />
        </Surface>
      </DropzoneWrapper>
    </FormControl>
  )
}

export default Description
